import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

/** Optional modules that are built but can be hidden in pilot (admin toggles in Innstillingar). */
export const DEFAULT_FEATURES = { map_enabled: false, parts_page_enabled: false };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("vms_user");
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState(() => {
    try {
      const f = localStorage.getItem("vms_features");
      return f ? { ...DEFAULT_FEATURES, ...JSON.parse(f) } : DEFAULT_FEATURES;
    } catch {
      return DEFAULT_FEATURES;
    }
  });

  const loadFeatures = async () => {
    try {
      const r = await api.get("/settings/features");
      const next = { ...DEFAULT_FEATURES, ...(r.data || {}) };
      setFeatures(next);
      localStorage.setItem("vms_features", JSON.stringify(next));
      return next;
    } catch {
      return features;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("vms_token");
    if (token) {
      api
        .get("/auth/me")
        .then((r) => {
          setUser(r.data);
          localStorage.setItem("vms_user", JSON.stringify(r.data));
          loadFeatures();
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("vms_token", r.data.access_token);
    localStorage.setItem("vms_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    loadFeatures();
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("vms_token");
    localStorage.removeItem("vms_user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAdmin: user?.role === "admin", features, setFeatures, reloadFeatures: loadFeatures }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
/** Convenience: `const { map_enabled } = useFeatures();` */
export const useFeatures = () => useContext(AuthContext)?.features || DEFAULT_FEATURES;
